/**
 * Voice Inputs API client (Feature B1, 2026-05-22).
 *
 * Wraps gateway endpoints (Subagent A · A2) for the per-card voice
 * input store:
 *   GET    /api/cards/:cardId/voice-inputs        — list voice inputs (items[])
 *   GET    /api/voice-inputs/:id/audio            — raw audio blob (auth cookie)
 *   POST   /api/voice-inputs/:id/transcribe       — kick off / retry transcribe
 *   DELETE /api/voice-inputs/:id                  — remove voice input + audio
 *
 * Pitfall reminder (M-X5): `apiJson` already prepends `VITE_API_BASE_URL`
 * which defaults to `/api`. Pass paths WITHOUT the `/api/` prefix. The
 * only place that hardcodes `/api/` is `audioUrl` because `<audio src>`
 * builds an absolute URL on its own and does not go through apiJson.
 *
 * Backend contract verified against Subagent A's voice-inputs.ts on
 * 2026-05-22. Field names mirror the actual server response:
 *   - transcriptStatus (NOT plain 'status')
 *   - transcriptError  (NOT 'errorMessage')
 *   - response wrapper `{ items: [...] }` (NOT `{ voiceInputs }`)
 */
import { env } from '../../env';
import { apiJson } from './client';

export type TranscriptStatus = 'pending' | 'transcribing' | 'done' | 'failed';

export interface VoiceInput {
  /** UUID — backing row id. */
  id: string;
  cardId: string;
  sessionId?: string | null;
  userId?: string | null;
  /** Original filename as captured by the recorder/upload. */
  filename: string;
  /** MIME type of the audio blob (audio/webm, audio/mpeg, audio/wav, ...). */
  mimeType: string | null;
  /** Bytes on disk (audio blob only). */
  sizeBytes: number;
  /** Duration in seconds, when known. May be null while pending. */
  durationSec: number | null;
  /** Plain-text transcript, when `transcriptStatus === 'done'`. */
  transcript: string | null;
  transcriptStatus: TranscriptStatus;
  /** Last error from the transcribe pipeline, when status === 'failed'. */
  transcriptError: string | null;
  createdAt: string;
  /** When transcription finished — null while pending/failed. */
  transcribedAt: string | null;
}

interface VoiceInputsListResponse {
  items: VoiceInput[];
  error?: string;
  message?: string;
}

export const voiceInputsApi = {
  /**
   * List every voice input attached to a card. Newest first.
   */
  list(cardId: string): Promise<VoiceInputsListResponse> {
    return apiJson<VoiceInputsListResponse>(`/cards/${cardId}/voice-inputs`);
  },

  /**
   * Stable URL for `<audio src>` / `<a download>`. Authenticated via
   * the shared httpOnly cookie — no header munging needed because the
   * gateway sets `credentials: 'include'` semantics on this origin.
   *
   * Note: this is the only place that hardcodes `/api/` because `<audio
   * src>` is not routed through `apiJson` and needs an absolute URL.
   */
  audioUrl(id: string): string {
    const base = env.VITE_API_BASE_URL.replace(/\/$/, '');
    return `${base}/voice-inputs/${id}/audio`;
  },

  /**
   * Trigger or retry transcription. Idempotent on the server side —
   * a row already at `done` returns the cached transcript without
   * re-running Whisper.
   */
  retranscribe(id: string): Promise<{ id: string; transcriptStatus: TranscriptStatus; transcript?: string }> {
    return apiJson(`/voice-inputs/${id}/transcribe`, { method: 'POST' });
  },

  /** Permanently delete a voice input (audio + transcript). */
  remove(id: string): Promise<{ ok: boolean }> {
    return apiJson(`/voice-inputs/${id}`, { method: 'DELETE' });
  },
};

export const voiceInputsKeys = {
  all: ['voice-inputs'] as const,
  list: (cardId: string) => [...voiceInputsKeys.all, 'list', cardId] as const,
  pending: (cardId: string) => [...voiceInputsKeys.all, 'pending', cardId] as const,
  one: (id: string) => [...voiceInputsKeys.all, 'one', id] as const,
};
